import { ResponseStatus } from "@/api/apiClient";
import { campaignCouponService } from "@/api/campaignCouponService";
import { campaignService } from "@/api/campaignService";
import { candidateService } from "@/api/candidateService";
import { packageCouponService } from "@/api/packageCouponService";
import { packageService } from "@/api/packageService";
import type { CampaignWithId } from "@/resources/campaign";
import type { Coupon, CouponListItem } from "@/resources/coupon";
import type { PackageWithId } from "@/resources/package";
import type { DateOrTimestamp } from "@/resources/utils";
import { CouponCopyError } from "@/utils/errorUtils";

export function isCouponCodeUnique(
  couponCode: string,
  couponList: CouponListItem[],
) {
  return !couponList.some((coupon) => coupon.couponCode === couponCode);
}

export async function copyPackageCouponsToCampaign(
  packageCoupons: Coupon[],
  packageId: string,
  campaignId: string,
) {
  if (packageCoupons.length === 0 || !packageId || !campaignId) {
    return;
  }

  const coupons = packageCoupons.map((coupon) => ({
    couponCode: coupon.couponCode,
    password: coupon.password,
    learnerId: coupon.learnerId ?? null,
    isConfirmed: coupon.learnerId ? true : false,
  }));

  const createdCoupons = await campaignCouponService.createMany(
    campaignId,
    coupons,
  );
  if (createdCoupons.length < coupons.length) {
    throw new CouponCopyError(
      `Failed to copy coupons to campaign ${campaignId}: created ${createdCoupons.length}/${coupons.length}`,
    );
  }

  const learnerTicketPromises = packageCoupons
    .filter(
      (coupon): coupon is Coupon & { learnerId: string } => !!coupon.learnerId,
    )
    .map((coupon) =>
      candidateService.createTicket(
        {
          couponCode: coupon.couponCode,
          couponPassword: coupon.password,
          learnerId: coupon.learnerId,
          package: packageId,
        },
        true,
      ),
    );

  try {
    await Promise.all(learnerTicketPromises);
  } catch (error) {
    console.error("Failed to create some learner tickets:", error);
  }

  const countUpdateResult = await campaignService.updateByIdOrCode(campaignId, {
    couponCount: packageCoupons.length,
  });
  if (
    countUpdateResult.status !== ResponseStatus.Ok ||
    !countUpdateResult.result
  ) {
    throw new CouponCopyError(
      `Failed to update coupon count for campaign ${campaignId}`,
    );
  }
}

export async function saveCoupons(
  relatedPackage: PackageWithId | null,
  relatedCampaigns: CampaignWithId[],
  importedCoupons: Partial<Coupon>[],
  updatedAt: DateOrTimestamp,
) {
  if (!relatedPackage || !importedCoupons || importedCoupons.length === 0) {
    return;
  }

  const createdCoupons = await packageCouponService.createMany(
    relatedPackage.id,
    importedCoupons.map((coupon) => ({
      couponCode: coupon.couponCode ?? "",
      password: coupon.password ?? "",
      learnerId: coupon.learnerId ?? null,
    })),
  );

  if (createdCoupons.length === 0) {
    throw new Error("Failed to create coupons");
  }

  const newCouponCount = createdCoupons.length + relatedPackage.couponCount;
  const updateResult = await packageService.updateByIdOrCode(
    relatedPackage.id,
    {
      couponCount: newCouponCount,
      updatedAt,
    },
  );
  if (!updateResult) {
    throw new Error("Failed to update package coupon count");
  }

  await Promise.all(
    relatedPackage.campaignIds.map(async (campaignId) => {
      const campaign = relatedCampaigns.find((c) => c.id === campaignId);
      if (!campaign) {
        console.error(`Campaign with ID ${campaignId} not found`);
        return;
      }
      const newCampaignCouponCount =
        createdCoupons.length + campaign.couponCount;
      await campaignService.updateByIdOrCode(campaignId, {
        couponCount: newCampaignCouponCount,
        updatedAt,
      });
    }),
  );

  if (createdCoupons.length < importedCoupons.length) {
    throw new Error(
      `Only ${createdCoupons.length} of ${importedCoupons.length} tickets were created`,
    );
  }
}
